import { useRouteLoaderData } from "react-router-dom";
import {
  hasUnlimitedSharedProfilesVisibility,
  isFreeOrganizationAccount,
  Organization,
} from "../../services/organization/organizationModel";
import { User } from "../../services/user/userModel";
import ProfileCard from "../candidate/profile/ProfileCardComponent";

import {
  compareBoolean,
  compareDate,
  multiSorts,
  useSearch,
} from "../../react-helpers/array";
import { useEffect, useMemo, useRef, useState } from "react";
import { cx } from "../../react-helpers/css";
import { organizationService } from "../../services/organization/organizationService";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import { ReturnButton } from "../../components/ReturnButton";
import InviteCandidatesDialog from "./InviteCandidatesDialog";
import SubscribeDialog from "./pro/SubscribeDialog";
import Pagination from "../../components/Pagination";
import proTutorialVideoFr from "../../assets/video/2mm_tutorial_pro_new.fr.mp4";
import proTutorialVideoEn from "../../assets/video/2mm_tutorial_pro_new.en.mp4";

// Only a French cut exists for now: every other language gets the English one
const proTutorialVideosByLang: Record<string, string> = {
  fr: proTutorialVideoFr,
};

enum Tab {
  Shared,
  Archived,
  Favorited,
}

const PAGE_SIZE = 12;
// Remember the current page across navigation so returning from a candidate
// profile lands back on the same page (kept for the browser tab session).
const PAGE_STORAGE_KEY = "organization-candidates-page";

const OrganizationCandidatesProfilesPage = () => {
  const { t, i18n } = useTranslation();
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const {
    archiveSharedProfile,
    unarchiveSharedProfile,
    favoriteSharedProfile,
    unfavoriteSharedProfile,
    deleteSharedProfile,
  } = organizationService();

  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  const [searchedProfiles, setSearch] = useSearch(
    organization.sharedProfiles,
    (e) => ({
      profile: {
        user: {
          lastname: e.profile.user?.lastname,
          firstname: e.profile.user?.firstname,
        },
      },
    }),
  );

  const sharedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.archivedAt === null)
      .sort(
        multiSorts(
          // put disabled profiles at the end of the list
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          // free plan: watchable videos first, locked history after the banner
          (a, b) => compareBoolean(!a.locked, !b.locked),
          // most recently shared first
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const archivedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.archivedAt !== null)
      .sort(
        multiSorts(
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const favoritedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.favoritedAt !== null && p.archivedAt === null)
      .sort(
        multiSorts(
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const [currentTab, setCurrentTab] = useState(Tab.Shared);
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [isSubscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const stored = Number(sessionStorage.getItem(PAGE_STORAGE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : 1;
  });

  // Persist the page so it can be restored when coming back to this page.
  useEffect(() => {
    sessionStorage.setItem(PAGE_STORAGE_KEY, String(currentPage));
  }, [currentPage]);

  const activeList = useMemo(() => {
    switch (currentTab) {
      case Tab.Favorited:
        return favoritedProfiles;
      case Tab.Archived:
        return archivedProfiles;
      default:
        return sharedProfiles;
    }
  }, [currentTab, sharedProfiles, archivedProfiles, favoritedProfiles]);

  const pageCount = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));

  const visibleProfiles = useMemo(
    () =>
      activeList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [activeList, currentPage],
  );

  // Free plan: the page splits around the unlock banner
  const visibleUnlockedProfiles = useMemo(
    () => visibleProfiles.filter((p) => !p.locked),
    [visibleProfiles],
  );
  const visibleLockedProfiles = useMemo(
    () => visibleProfiles.filter((p) => p.locked),
    [visibleProfiles],
  );

  // Reset to the first page whenever the tab changes (but not on the initial
  // render, so a restored page survives a return navigation).
  const previousTab = useRef(currentTab);
  useEffect(() => {
    if (previousTab.current !== currentTab) {
      previousTab.current = currentTab;
      setCurrentPage(1);
    }
  }, [currentTab]);

  // Keep the current page within bounds when the list shrinks (search,
  // archive/favorite/delete actions, …).
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount));
  }, [pageCount]);

  const onSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const isFreeAccount = useMemo(
    () => isFreeOrganizationAccount(organization, loggedUser),
    [organization, loggedUser],
  );

  // Invited member of an organization: the backend already unlocks the whole
  // history, so the freemium "limited to the 3 last profiles" banner would be
  // wrong even though the account has no paid subscription.
  const hasUnlimitedVisibility = useMemo(
    () => hasUnlimitedSharedProfilesVisibility(loggedUser),
    [loggedUser],
  );

  // Shown to every free account, whatever the number of (locked) videos —
  // except invited members: their history is already fully unlocked, so the
  // "unlock your history" pitch would be meaningless.
  const proTutorialVideoSrc =
    proTutorialVideosByLang[i18n.language.split("-")[0]] ?? proTutorialVideoEn;

  const unlockHistoryBanner = isFreeAccount && !hasUnlimitedVisibility && (
    <div className="lblock--l pro-banner grid-2-1">
      <div>
        <h2 className="pro-banner_title">
          {t("organization:candidates-profiles.UNLOCK_HISTORY_TITLE")}
        </h2>
        <div className="cblock">
          <div>
            {t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_1")}
          </div>
          <div>
            {t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_2")}
          </div>
          <div>
            {t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_3")}
          </div>
        </div>
        <div className="cblock lrow --gap--s --center--v">
          <a
            className="btn--white"
            href="https://www.2minutesmax.com/espace-professionnel/"
            target="_blank"
            rel="noreferrer"
          >
            {t("organization:candidates-profiles.DISCOVER_WORKSPACE_PRO")}
          </a>
          {loggedUser.adminOrga ? (
            <button
              className="btn--1"
              onClick={() => setSubscribeDialogOpen(true)}
            >
              {t("organization:candidates-profiles.TRY_PRO_CTA")}
            </button>
          ) : (
            <div>
              {t("organization:candidates-profiles.LOCKED_CONTACT_ADMIN")}
            </div>
          )}
        </div>
      </div>
      <video
        className="pro-banner_video"
        src={proTutorialVideoSrc}
        controls
        playsInline
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );

  const renderSharedProfileCard = (
    profile: Organization["sharedProfiles"][number],
  ) => (
    <ProfileCard
      key={profile.profileId}
      profile={profile.profile}
      locked={!!profile.locked}
      canUpgrade={loggedUser.adminOrga}
      onUpgrade={() => setSubscribeDialogOpen(true)}
      isArchived={!!profile.archivedAt}
      // Archiving and favorites are PRO features: no action icons on free plan
      onArchive={
        isFreeAccount
          ? undefined
          : () => {
              confirm(
                "organization:shared-profile.archive.CONFIRM",
                () =>
                  archiveSharedProfile(
                    profile.profile.uid,
                    organization.id,
                  ).then(() => {
                    reload();
                  }),
                true,
              );
            }
      }
      isFavorited={!!profile.favoritedAt}
      onFavorite={
        isFreeAccount
          ? undefined
          : async () => {
              await favoriteSharedProfile(profile.profile.uid, organization.id);
              reload();
            }
      }
      onUnfavorite={
        isFreeAccount
          ? undefined
          : async () => {
              await unfavoriteSharedProfile(
                profile.profile.uid,
                organization.id,
              );
              reload();
            }
      }
    />
  );

  useEffect(() => {
    if (currentTab === Tab.Archived && archivedProfiles.length === 0) {
      setCurrentTab(Tab.Shared);
    }
  }, [archivedProfiles.length, currentTab]);

  useEffect(() => {
    if (currentTab === Tab.Favorited && favoritedProfiles.length === 0) {
      setCurrentTab(Tab.Shared);
    }
  }, [favoritedProfiles.length, currentTab]);

  return (
    <div className="layout_content --bg--organization-dahsboard">
      <div className="page-content container --wrap">
        {!isFreeAccount && (
          <div>
            <ReturnButton forcedUrl="/organization">
              {t("organization:RETURN_TO_RECRUITER_DASHBOARD")}
            </ReturnButton>
          </div>
        )}

        <div className="page_head lblock">
          <h2 className="section_title">
            {t("organization:candidates-profiles.MY_CANDIDATE_PROFILES")}
          </h2>
        </div>

        {isFreeAccount && !hasUnlimitedVisibility && (
          <div className="lblock info">
            <strong>
              {t("organization:candidates-profiles.FREEMIUM_TITLE")}
            </strong>
            <div>{t("organization:candidates-profiles.FREEMIUM_TEXT")}</div>
          </div>
        )}

        {(archivedProfiles.length > 0 || favoritedProfiles.length > 0) && (
          <div className="lblock tabs">
            <button
              className={cx([
                "tab_item",
                currentTab === Tab.Shared && "active",
              ])}
              onClick={() => setCurrentTab(Tab.Shared)}
            >
              {t("organization:candidates-profiles.SHARED_PROFILES")}
            </button>
            {favoritedProfiles.length > 0 && (
              <button
                className={cx([
                  "tab_item",
                  currentTab === Tab.Favorited && "active",
                ])}
                onClick={() => setCurrentTab(Tab.Favorited)}
              >
                {t("organization:candidates-profiles.FAVORITED_PROFILES")}
              </button>
            )}
            {archivedProfiles.length > 0 && (
              <button
                className={cx([
                  "tab_item",
                  currentTab === Tab.Archived && "active",
                ])}
                onClick={() => setCurrentTab(Tab.Archived)}
              >
                {t("organization:candidates-profiles.ARCHIVED_PROFILES")}
              </button>
            )}
          </div>
        )}

        {organization.sharedProfiles.length === 0 && (
          <>
            <div className="lblock--l info">
              {t("organization:candidates-profiles.NO_PROFILES_YET")}
              <div>
                {t("organization:candidates-profiles.NO_PROFILES_MESSAGE")}
              </div>
            </div>
            {unlockHistoryBanner}
          </>
        )}

        <div className="lblock--l info --success invitation">
          <div>
            <strong>
              {t("organization:candidates-profiles.INVITE_CANDIDATES")}
            </strong>
            <div>
              {t("organization:candidates-profiles.INVITE_MAIL_MESSAGE")}
            </div>
          </div>
          <div>
            <a
              className="btn --block active"
              aria-current="page"
              onClick={() => setInviteDialogOpen(true)}
            >
              {t("organization:candidates-profiles.INVITE_BUTTON")}
            </a>
          </div>
        </div>

        {organization.sharedProfiles.length > 0 && (
          <>
            <div className="lblock--l grid-3">
              <input
                type="text"
                className="input"
                placeholder={t(
                  "organization:candidates-profiles.SEARCH_PROFILE",
                )}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
            {currentTab === Tab.Shared && (
              <>
                {visibleUnlockedProfiles.length > 0 && (
                  <div className="lblock--l grid --fill">
                    {visibleUnlockedProfiles.map(renderSharedProfileCard)}
                  </div>
                )}
                {unlockHistoryBanner}
                {visibleLockedProfiles.length > 0 && (
                  <div className="lblock--l grid --fill">
                    {visibleLockedProfiles.map(renderSharedProfileCard)}
                  </div>
                )}
              </>
            )}
            {currentTab !== Tab.Shared && (
              <div className="lblock--l grid --fill">
                {currentTab === Tab.Favorited &&
                  visibleProfiles.map((profile) => (
                    <ProfileCard
                      key={profile.profileId}
                      profile={profile.profile}
                      locked={!!profile.locked}
                      canUpgrade={loggedUser.adminOrga}
                      onUpgrade={() => setSubscribeDialogOpen(true)}
                      isArchived={!!profile.archivedAt}
                      onArchive={
                        isFreeAccount
                          ? undefined
                          : () => {
                              confirm(
                                "organization:shared-profile.archive.CONFIRM",
                                () =>
                                  archiveSharedProfile(
                                    profile.profile.uid,
                                    organization.id,
                                  ).then(() => {
                                    reload();
                                  }),
                                true,
                              );
                            }
                      }
                      isFavorited={!!profile.favoritedAt}
                      onUnfavorite={
                        isFreeAccount
                          ? undefined
                          : async () => {
                              await unfavoriteSharedProfile(
                                profile.profile.uid,
                                organization.id,
                              );
                              reload();
                            }
                      }
                    />
                  ))}
                {currentTab === Tab.Archived &&
                  visibleProfiles.map((profile) => (
                    <ProfileCard
                      key={profile.profileId}
                      profile={profile.profile}
                      locked={!!profile.locked}
                      canUpgrade={loggedUser.adminOrga}
                      onUpgrade={() => setSubscribeDialogOpen(true)}
                      isArchived={!!profile.archivedAt}
                      onUnarchive={
                        isFreeAccount
                          ? undefined
                          : () => {
                              confirm(
                                "organization:shared-profile.unarchive.CONFIRM",
                                () =>
                                  unarchiveSharedProfile(
                                    profile.profile.uid,
                                    organization.id,
                                  ).then(() => {
                                    reload();
                                  }),
                                true,
                              );
                            }
                      }
                      onDelete={() => {
                        confirm(
                          "organization:shared-profile.delete.CONFIRM",
                          () =>
                            deleteSharedProfile(
                              profile.profile.uid,
                              organization.id,
                            ).then(() => {
                              reload();
                            }),
                          true,
                        );
                      }}
                    />
                  ))}
              </div>
            )}
            <Pagination
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
      {isInviteDialogOpen && (
        <InviteCandidatesDialog
          organization={organization}
          onClose={() => setInviteDialogOpen(false)}
        />
      )}
      {isSubscribeDialogOpen && (
        <SubscribeDialog
          organization={organization}
          onClose={() => setSubscribeDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default OrganizationCandidatesProfilesPage;
